/*
  Rahu Care Hair Oil Experience - storefront client
  ------------------------------------------------------------------
  Collects email + first name (Step 1) and the 6 quiz answers, then POSTs
  them to the consultation endpoint (Shopify App Proxy → Vercel). The
  server scores the answers against the five formulas and saves the result
  to the customer. This file holds NO scoring or formulation logic - the IP
  stays server-side, and neither the formula's name nor its style/description
  is ever shown to the customer, only a confirmation that it's on file.
*/

(() => {
  'use strict';

  // Simple inline line icons for the hair-type options (currentColor, no fill).
  const svg = (paths) => `<svg class="hair-experience__icon" viewBox="0 0 48 48" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">${paths}</svg>`;
  const ICONS = {
    straight: svg('<path d="M14 6v36M24 6v36M34 6v36"/>'),
    wavy: svg('<path d="M14 6c-6 5 6 10 0 15s6 10 0 15"/><path d="M24 6c-6 5 6 10 0 15s6 10 0 15"/><path d="M34 6c-6 5 6 10 0 15s6 10 0 15"/>'),
    curly: svg('<path d="M16 8c8 0 8 8 0 8s-8 8 0 8 8 8 0 8"/><path d="M30 8c8 0 8 8 0 8s-8 8 0 8 8 8 0 8"/>'),
    coily: svg('<path d="M13 6l8 5-8 5 8 5-8 5 8 5-8 5"/><path d="M31 6l-8 5 8 5-8 5 8 5-8 5 8 5"/>'),
  };

  // Questions (UI only - scoring lives server-side). Values (A–D) must match the server.
  const QUESTIONS = [
    {
      id: 'q1', eyebrow: 'Your hair', question: 'How would you describe a single strand of your hair?',
      hint: 'One of the most important questions for the blend.',
      options: [
        { value: 'A', label: 'Fine - almost invisible, delicate' },
        { value: 'B', label: 'Medium - normal, average thickness' },
        { value: 'C', label: 'Thick or coarse - a strong, visible strand' },
        { value: 'D', label: 'Mixed - fine in some areas, thicker in others' },
      ],
    },
    {
      id: 'q2', eyebrow: 'Your hair', question: 'What is your natural hair texture?', hint: '',
      options: [
        { value: 'A', label: 'Straight - no wave at all', icon: ICONS.straight },
        { value: 'B', label: 'Soft wave or gentle S-shape', icon: ICONS.wavy },
        { value: 'C', label: 'Defined waves or loose curls', icon: ICONS.curly },
        { value: 'D', label: 'Tight curls or coils', icon: ICONS.coily },
      ],
    },
    {
      id: 'q3', eyebrow: 'Your hair', question: 'What is your hair’s chemical history?', hint: '',
      options: [
        { value: 'A', label: 'Virgin - never colored or chemically treated' },
        { value: 'B', label: 'Light highlights or subtle toning' },
        { value: 'C', label: 'Full color, bleached or heavily highlighted' },
        { value: 'D', label: 'Permed, relaxed or chemically straightened' },
      ],
    },
    {
      id: 'q4', eyebrow: 'Wash day', question: 'Right after washing - before applying anything - how does your hair feel?', hint: '',
      options: [
        { value: 'A', label: 'Flat and limp, no volume' },
        { value: 'B', label: 'Normal and easy to manage' },
        { value: 'C', label: 'Dry at the ends, roots feel fine' },
        { value: 'D', label: 'Dry, frizzy or rough all over' },
      ],
    },
    {
      id: 'q5', eyebrow: 'Your concern', question: 'What is your main hair concern?', hint: '',
      options: [
        { value: 'A', label: 'My hair gets flat or greasy fast' },
        { value: 'B', label: 'I want more everyday softness and shine' },
        { value: 'C', label: 'My color looks dull and my ends feel dry' },
        { value: 'D', label: 'Frizz, undefined texture or curl control' },
      ],
    },
    {
      id: 'q6', eyebrow: 'The result', question: 'What result matters most to you from a hair oil?', hint: '',
      options: [
        { value: 'A', label: 'Weightless - I want to feel it, not see it' },
        { value: 'B', label: 'Soft and polished with a natural finish' },
        { value: 'C', label: 'High-gloss, mirror-like salon shine' },
        { value: 'D', label: 'Deep nourishment - repaired and smooth' },
      ],
    },
  ];

  class HairExperience {
    constructor(root) {
      this.root = root;
      this.endpoint = root.dataset.endpoint || '';
      this.sharedKey = root.dataset.key || '';
      this.state = { email: '', firstName: '', answers: {} };
      this.render();
    }

    steps() {
      const list = [{ kind: 'email' }];
      QUESTIONS.forEach((q) => list.push({ kind: 'question', q }));
      list.push({ kind: 'review' });
      return list;
    }

    render() {
      this.root.innerHTML = `
        <ol class="hair-experience__progress" aria-hidden="true"></ol>
        <div class="hair-experience__body" aria-live="polite"></div>`;
      this.body = this.root.querySelector('.hair-experience__body');
      this.progressEl = this.root.querySelector('.hair-experience__progress');
      this.index = 0;
      this.show(0);
    }

    updateProgress(steps) {
      this.progressEl.innerHTML = steps.map(() => '<li></li>').join('');
      [...this.progressEl.children].forEach((li, i) => {
        li.toggleAttribute('data-done', i < this.index);
        li.toggleAttribute('data-current', i === this.index);
      });
    }

    show(i) {
      const steps = this.steps();
      this.index = Math.max(0, Math.min(i, steps.length - 1));
      const step = steps[this.index];
      this.updateProgress(steps);
      if (step.kind === 'email') this.showEmail();
      else if (step.kind === 'question') this.showQuestion(step.q);
      else this.showReview();
      this.root.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }

    navWrap(inner, withBack) {
      return `<section class="hair-experience__step" data-active>${inner}
        <div class="hair-experience__nav">
          <button type="button" class="hair-experience__back"${withBack ? '' : ' hidden'}>Back</button>
        </div></section>`;
    }

    bindBack() {
      const back = this.body.querySelector('.hair-experience__back');
      if (back) back.addEventListener('click', () => this.show(this.index - 1));
    }

    showEmail() {
      this.body.innerHTML = this.navWrap(`
        <span class="hair-experience__eyebrow">Your consultation</span>
        <h2 class="hair-experience__question h4">Let’s start with your details</h2>
        <p class="hair-experience__hint">We’ll prepare your considered formula and keep it on file for you.</p>
        <div class="hair-experience__fields">
          <label class="hair-experience__field"><span>First name</span>
            <input type="text" name="firstName" autocomplete="given-name" value="${escapeAttr(this.state.firstName)}"></label>
          <label class="hair-experience__field"><span>Email</span>
            <input type="email" name="email" autocomplete="email" inputmode="email" required value="${escapeAttr(this.state.email)}"></label>
        </div>
        <button type="button" class="button w-full" data-hxp-continue>Begin</button>
        <p class="hair-experience__error" hidden></p>`, false);

      const emailInput = this.body.querySelector('input[name="email"]');
      const nameInput = this.body.querySelector('input[name="firstName"]');
      this.body.querySelector('[data-hxp-continue]').addEventListener('click', () => {
        const email = emailInput.value.trim();
        if (!isValidEmail(email)) { this.showError('Please enter a valid email so we can save your consultation.'); emailInput.focus(); return; }
        this.state.email = email;
        this.state.firstName = nameInput.value.trim();
        this.show(this.index + 1);
      });
      this.bindBack();
    }

    showQuestion(q) {
      const hasIcons = q.options.some((o) => o.icon);
      const layout = hasIcons ? ' hair-experience__options--two hair-experience__options--icons' : (q.options.length >= 4 ? ' hair-experience__options--two' : '');
      this.body.innerHTML = this.navWrap(`
        <span class="hair-experience__eyebrow">${q.eyebrow}</span>
        <h2 class="hair-experience__question h4">${q.question}</h2>
        ${q.hint ? `<p class="hair-experience__hint">${q.hint}</p>` : '<div style="height:1rem"></div>'}
        <div class="hair-experience__options${layout}" role="group" aria-label="${escapeAttr(q.question)}">
          ${q.options.map((o) => `
            <button type="button" class="hair-experience__option" data-value="${o.value}" aria-pressed="${this.state.answers[q.id] === o.value}">
              ${o.icon || ''}
              <span class="hair-experience__option-label">${o.label}</span>
            </button>`).join('')}
        </div>`, true);

      this.body.querySelectorAll('.hair-experience__option').forEach((btn) => {
        btn.addEventListener('click', () => {
          this.state.answers[q.id] = btn.dataset.value;
          this.body.querySelectorAll('.hair-experience__option').forEach((b) => b.setAttribute('aria-pressed', b === btn));
          window.setTimeout(() => this.show(this.index + 1), 200);
        });
      });
      this.bindBack();
    }

    showReview() {
      this.body.innerHTML = this.navWrap(`
        <span class="hair-experience__eyebrow">Almost there</span>
        <h2 class="hair-experience__question h4">Ready to submit your consultation</h2>
        <p class="hair-experience__hint">We’ll prepare your considered formula and keep it on file for ${escapeHtml(this.state.email)}. Our team blends it to order in Estonia.</p>
        <button type="button" class="button w-full" data-hxp-submit>Make it yours</button>
        <p class="hair-experience__error" hidden></p>`, true);
      this.body.querySelector('[data-hxp-submit]').addEventListener('click', (e) => this.submit(e.currentTarget));
      this.bindBack();
    }

    showSubmitted() {
      this.progressEl.innerHTML = '';
      this.body.innerHTML = `
        <section class="hair-experience__step" data-active>
          <span class="hair-experience__eyebrow">Thank you - your answers have been submitted</span>
          <h2 class="hair-experience__question h4">Your consultation is saved</h2>
          <p class="hair-experience__hint">We’ve prepared and saved your bespoke formula. Our team blends it to order in Estonia and it will be on file the next time you reorder.</p>
        </section>`;
    }

    showError(msg) {
      const el = this.body.querySelector('.hair-experience__error');
      if (el) { el.textContent = msg; el.hidden = false; }
    }

    async submit(btn) {
      btn.disabled = true;
      const original = btn.textContent;
      btn.textContent = 'Submitting…';
      try {
        const res = await fetch(this.endpoint, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json',
            ...(this.sharedKey ? { 'X-Consultation-Key': this.sharedKey } : {}),
          },
          body: JSON.stringify({
            email: this.state.email,
            firstName: this.state.firstName,
            answers: this.state.answers,
          }),
        });
        const data = await res.json().catch(() => ({}));
        if (!res.ok || !data.ok) throw new Error(data.error || 'Could not save your consultation. Please try again.');
        this.showSubmitted();
      } catch (e) {
        this.showError(e.message);
        btn.disabled = false;
        btn.textContent = original;
      }
    }
  }

  function isValidEmail(v) { return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v); }
  function escapeAttr(s) { return String(s || '').replace(/"/g, '&quot;').replace(/</g, '&lt;'); }
  function escapeHtml(s) { return String(s || '').replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;'); }

  function init() {
    document.querySelectorAll('[data-hair-experience]').forEach((el) => {
      if (!el.dataset.hxpInit) { el.dataset.hxpInit = '1'; new HairExperience(el); }
    });
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();
  document.addEventListener('shopify:section:load', init);
})();
